/**
 * Centralized Theme Configuration
 *
 * All app colors and styles are defined here.
 * Update this file to change the entire app theme.
 */

export type ThemeName = 'dark' | 'light' | 'ocean' | 'sunset' | 'forest' | 'cyberpunk';

export interface ThemeConfig {
  sidebar: {
    background: string;
    text: string;
    border: string;
    textSecondary: string;
    textTertiary: string;
    hoverBg: string;
    hoverText: string;
    sectionLabel: string;
    collapsed: {
      width: string;
      expandedWidth: string;
    };
  };
  header: {
    background: string;
    border: string;
    text: string;
    logo: string;
    iconColor: string;
    iconHover: string;
    iconHoverText: string;
  };
  layout: {
    background: string;
    contentBackground: string;
    card: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    textMuted: string;
  };
  dropdown: {
    background: string;
    border: string;
    text: string;
    textSecondary: string;
    hoverBg: string;
    hoverText: string;
    activeBg: string;
    activeText: string;
    headerBg: string;
    divider: string;
    shadow: string;
  };
  accent: {
    primary: string;
    primaryText: string;
    primaryHover: string;
  };
  badge: {
    background: string;
    text: string;
  };
  form: {
    bg: string;
    sectionBg: string;
    sectionBorder: string;
    inputBg: string;
    inputBorder: string;
    inputText: string;
    labelText: string;
    helperText: string;
    titleText: string;
    closeBtn: string;
  };
}

export const themes: Record<ThemeName, ThemeConfig> = {

  // ── Dark — Obsidian Academy ─────────────────────────────────
  dark: {
    sidebar: {
      background: 'bg-[#0d1117]',
      text: 'text-slate-300',
      border: 'border-[#1e2a3a]',
      textSecondary: 'text-slate-400',
      textTertiary: 'text-slate-500',
      hoverBg: 'hover:bg-[#161f2e]',
      hoverText: 'hover:text-white',
      sectionLabel: 'text-slate-600',
      collapsed: { width: 'md:w-16', expandedWidth: 'md:w-60' },
    },
    header: {
      background: 'bg-[#0d1117]',
      border: 'border-[#1e2a3a]',
      text: 'text-slate-200',
      logo: 'text-amber-400',
      iconColor: 'text-slate-400',
      iconHover: 'hover:bg-[#161f2e]',
      iconHoverText: 'hover:text-white',
    },
    layout: {
      background: 'bg-[#060a12]',
      contentBackground: 'bg-[#0d1117]',
      card: 'bg-[#111827]',
      cardBorder: 'border-[#1e2a3a]',
      text: 'text-slate-100',
      textSecondary: 'text-slate-400',
      textMuted: 'text-slate-500',
    },
    dropdown: {
      background: 'bg-[#111827]',
      border: 'border-[#1e2a3a]',
      text: 'text-slate-200',
      textSecondary: 'text-slate-400',
      hoverBg: 'hover:bg-[#161f2e]',
      hoverText: 'hover:text-white',
      activeBg: 'bg-amber-500/10',
      activeText: 'text-amber-300',
      headerBg: 'bg-[#0d1117]',
      divider: 'border-[#1e2a3a]',
      shadow: 'shadow-2xl shadow-black/50',
    },
    accent: {
      primary: 'bg-amber-500',
      primaryText: 'text-amber-400',
      primaryHover: 'hover:bg-amber-600',
    },
    badge: { background: 'bg-amber-500', text: 'text-black' },
    form: {
      bg: 'bg-[#111827]',
      sectionBg: 'bg-[#0d1117]',
      sectionBorder: 'border-[#1e2a3a]',
      inputBg: 'bg-[#0d1117]',
      inputBorder: 'border-[#1e2a3a]',
      inputText: 'text-slate-100',
      labelText: 'text-slate-300',
      helperText: 'text-slate-500',
      titleText: 'text-slate-100',
      closeBtn: 'text-slate-500 hover:text-slate-200',
    },
  },

  // ── Light ───────────────────────────────────────────────────
  light: {
    sidebar: {
      background: 'bg-white',
      text: 'text-slate-700',
      border: 'border-slate-200',
      textSecondary: 'text-slate-500',
      textTertiary: 'text-slate-400',
      hoverBg: 'hover:bg-slate-100',
      hoverText: 'hover:text-slate-900',
      sectionLabel: 'text-slate-400',
      collapsed: { width: 'md:w-20', expandedWidth: 'md:w-64' },
    },
    header: {
      background: 'bg-white',
      border: 'border-slate-200',
      text: 'text-slate-900',
      logo: 'text-sky-500',
      iconColor: 'text-slate-500',
      iconHover: 'hover:bg-slate-100',
      iconHoverText: 'hover:text-slate-900',
    },
    layout: {
      background: 'bg-slate-100',
      contentBackground: 'bg-slate-50',
      card: 'bg-white',
      cardBorder: 'border-slate-200',
      text: 'text-slate-900',
      textSecondary: 'text-slate-600',
      textMuted: 'text-slate-400',
    },
    dropdown: {
      background: 'bg-white',
      border: 'border-slate-200',
      text: 'text-slate-700',
      textSecondary: 'text-slate-500',
      hoverBg: 'hover:bg-slate-100',
      hoverText: 'hover:text-slate-900',
      activeBg: 'bg-sky-50',
      activeText: 'text-sky-600',
      headerBg: 'bg-slate-50',
      divider: 'border-slate-200',
      shadow: 'shadow-xl shadow-slate-200/80',
    },
    accent: {
      primary: 'bg-sky-500',
      primaryText: 'text-sky-500',
      primaryHover: 'hover:bg-sky-600',
    },
    badge: { background: 'bg-sky-500', text: 'text-white' },
    form: {
      bg: 'bg-white',
      sectionBg: 'bg-slate-100',
      sectionBorder: 'border-slate-200',
      inputBg: 'bg-white',
      inputBorder: 'border-slate-300',
      inputText: 'text-slate-900',
      labelText: 'text-slate-800',
      helperText: 'text-slate-500',
      titleText: 'text-slate-900',
      closeBtn: 'text-slate-400 hover:text-slate-700',
    },
  },

  // ── Ocean ───────────────────────────────────────────────────
  ocean: {
    sidebar: {
      background: 'bg-blue-950',
      text: 'text-cyan-100',
      border: 'border-blue-800',
      textSecondary: 'text-cyan-300',
      textTertiary: 'text-blue-400',
      hoverBg: 'hover:bg-blue-800',
      hoverText: 'hover:text-white',
      sectionLabel: 'text-blue-500',
      collapsed: { width: 'md:w-20', expandedWidth: 'md:w-64' },
    },
    header: {
      background: 'bg-blue-900',
      border: 'border-blue-700',
      text: 'text-white',
      logo: 'text-cyan-300',
      iconColor: 'text-cyan-200',
      iconHover: 'hover:bg-blue-800',
      iconHoverText: 'hover:text-white',
    },
    layout: {
      background: 'bg-slate-950',
      contentBackground: 'bg-slate-900',
      card: 'bg-blue-950',
      cardBorder: 'border-blue-800',
      text: 'text-cyan-50',
      textSecondary: 'text-cyan-300',
      textMuted: 'text-blue-400',
    },
    dropdown: {
      background: 'bg-blue-900',
      border: 'border-blue-700',
      text: 'text-cyan-100',
      textSecondary: 'text-cyan-300',
      hoverBg: 'hover:bg-blue-800',
      hoverText: 'hover:text-white',
      activeBg: 'bg-cyan-900',
      activeText: 'text-cyan-300',
      headerBg: 'bg-blue-950',
      divider: 'border-blue-800',
      shadow: 'shadow-2xl',
    },
    accent: {
      primary: 'bg-cyan-600',
      primaryText: 'text-cyan-400',
      primaryHover: 'hover:bg-cyan-700',
    },
    badge: { background: 'bg-orange-500', text: 'text-white' },
    form: {
      bg: 'bg-blue-950',
      sectionBg: 'bg-slate-950',
      sectionBorder: 'border-blue-800',
      inputBg: 'bg-blue-900',
      inputBorder: 'border-blue-700',
      inputText: 'text-cyan-100',
      labelText: 'text-cyan-200',
      helperText: 'text-blue-400',
      titleText: 'text-cyan-100',
      closeBtn: 'text-blue-400 hover:text-cyan-200',
    },
  },

  // ── Sunset ──────────────────────────────────────────────────
  sunset: {
    sidebar: {
      background: 'bg-red-950',
      text: 'text-orange-100',
      border: 'border-red-800',
      textSecondary: 'text-orange-300',
      textTertiary: 'text-red-400',
      hoverBg: 'hover:bg-red-800',
      hoverText: 'hover:text-white',
      sectionLabel: 'text-red-500',
      collapsed: { width: 'md:w-20', expandedWidth: 'md:w-64' },
    },
    header: {
      background: 'bg-red-900',
      border: 'border-red-700',
      text: 'text-white',
      logo: 'text-orange-300',
      iconColor: 'text-orange-200',
      iconHover: 'hover:bg-red-800',
      iconHoverText: 'hover:text-white',
    },
    layout: {
      background: 'bg-gray-950',
      contentBackground: 'bg-gray-900',
      card: 'bg-red-950',
      cardBorder: 'border-red-800',
      text: 'text-orange-50',
      textSecondary: 'text-orange-300',
      textMuted: 'text-red-400',
    },
    dropdown: {
      background: 'bg-red-900',
      border: 'border-red-700',
      text: 'text-orange-100',
      textSecondary: 'text-orange-300',
      hoverBg: 'hover:bg-red-800',
      hoverText: 'hover:text-white',
      activeBg: 'bg-orange-900',
      activeText: 'text-orange-300',
      headerBg: 'bg-red-950',
      divider: 'border-red-800',
      shadow: 'shadow-2xl',
    },
    accent: {
      primary: 'bg-orange-600',
      primaryText: 'text-orange-400',
      primaryHover: 'hover:bg-orange-700',
    },
    badge: { background: 'bg-red-500', text: 'text-white' },
    form: {
      bg: 'bg-red-950',
      sectionBg: 'bg-gray-950',
      sectionBorder: 'border-red-800',
      inputBg: 'bg-red-900',
      inputBorder: 'border-red-700',
      inputText: 'text-orange-100',
      labelText: 'text-orange-200',
      helperText: 'text-red-400',
      titleText: 'text-orange-100',
      closeBtn: 'text-red-400 hover:text-orange-200',
    },
  },

  // ── Forest ──────────────────────────────────────────────────
  forest: {
    sidebar: {
      background: 'bg-green-950',
      text: 'text-green-100',
      border: 'border-green-800',
      textSecondary: 'text-green-300',
      textTertiary: 'text-green-500',
      hoverBg: 'hover:bg-green-800',
      hoverText: 'hover:text-white',
      sectionLabel: 'text-green-600',
      collapsed: { width: 'md:w-20', expandedWidth: 'md:w-64' },
    },
    header: {
      background: 'bg-green-900',
      border: 'border-green-700',
      text: 'text-white',
      logo: 'text-emerald-300',
      iconColor: 'text-green-200',
      iconHover: 'hover:bg-green-800',
      iconHoverText: 'hover:text-white',
    },
    layout: {
      background: 'bg-gray-950',
      contentBackground: 'bg-gray-900',
      card: 'bg-green-950',
      cardBorder: 'border-green-800',
      text: 'text-green-50',
      textSecondary: 'text-green-300',
      textMuted: 'text-green-500',
    },
    dropdown: {
      background: 'bg-green-900',
      border: 'border-green-700',
      text: 'text-green-100',
      textSecondary: 'text-green-300',
      hoverBg: 'hover:bg-green-800',
      hoverText: 'hover:text-white',
      activeBg: 'bg-emerald-900',
      activeText: 'text-emerald-300',
      headerBg: 'bg-green-950',
      divider: 'border-green-800',
      shadow: 'shadow-2xl',
    },
    accent: {
      primary: 'bg-emerald-600',
      primaryText: 'text-emerald-400',
      primaryHover: 'hover:bg-emerald-700',
    },
    badge: { background: 'bg-lime-500', text: 'text-white' },
    form: {
      bg: 'bg-green-950',
      sectionBg: 'bg-gray-950',
      sectionBorder: 'border-green-800',
      inputBg: 'bg-green-900',
      inputBorder: 'border-green-700',
      inputText: 'text-green-100',
      labelText: 'text-green-200',
      helperText: 'text-green-500',
      titleText: 'text-green-100',
      closeBtn: 'text-green-500 hover:text-green-200',
    },
  },

  // ── Cyberpunk ───────────────────────────────────────────────
  cyberpunk: {
    sidebar: {
      background: 'bg-purple-950',
      text: 'text-pink-100',
      border: 'border-purple-700',
      textSecondary: 'text-pink-300',
      textTertiary: 'text-purple-400',
      hoverBg: 'hover:bg-purple-800',
      hoverText: 'hover:text-white',
      sectionLabel: 'text-purple-500',
      collapsed: { width: 'md:w-20', expandedWidth: 'md:w-64' },
    },
    header: {
      background: 'bg-purple-900',
      border: 'border-purple-700',
      text: 'text-pink-100',
      logo: 'text-pink-400',
      iconColor: 'text-pink-200',
      iconHover: 'hover:bg-purple-800',
      iconHoverText: 'hover:text-white',
    },
    layout: {
      background: 'bg-gray-950',
      contentBackground: 'bg-gray-900',
      card: 'bg-purple-950',
      cardBorder: 'border-purple-800',
      text: 'text-pink-50',
      textSecondary: 'text-pink-300',
      textMuted: 'text-purple-400',
    },
    dropdown: {
      background: 'bg-purple-900',
      border: 'border-purple-700',
      text: 'text-pink-100',
      textSecondary: 'text-pink-300',
      hoverBg: 'hover:bg-purple-800',
      hoverText: 'hover:text-white',
      activeBg: 'bg-pink-900',
      activeText: 'text-pink-300',
      headerBg: 'bg-slate-950',
      divider: 'border-purple-700',
      shadow: 'shadow-2xl',
    },
    accent: {
      primary: 'bg-pink-600',
      primaryText: 'text-pink-400',
      primaryHover: 'hover:bg-pink-700',
    },
    badge: { background: 'bg-pink-500', text: 'text-white' },
    form: {
      bg: 'bg-purple-950',
      sectionBg: 'bg-gray-950',
      sectionBorder: 'border-purple-700',
      inputBg: 'bg-purple-900',
      inputBorder: 'border-purple-600',
      inputText: 'text-pink-100',
      labelText: 'text-pink-200',
      helperText: 'text-purple-400',
      titleText: 'text-pink-100',
      closeBtn: 'text-purple-400 hover:text-pink-200',
    },
  },
};

// Default theme export
export const theme = themes.dark;

// Helper to combine theme classes
export const themeClass = (...classes: string[]) => classes.filter(Boolean).join(' ');
